import { Component, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { SocketService } from '../../services/socket.service';
import { ChatService } from '../../services/chat.service';

@Component({
  selector: 'app-login',
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './login.html',
  styleUrl: './login.css',
})
export class Login implements OnInit {
  signupForm: FormGroup;
  activeTab = signal<'signup' | 'login'>('login');
  isLoading = signal(false);
  errorMessage = signal('');
  showSessionDialog = signal(false);
  sessionInfo = signal<{fecha: string, device: string} | null>(null);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService,
    private socketService: SocketService,
    private chatService: ChatService
  ) {
    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      terms: [true] // Opcional, ya marcado por defecto
    });
  }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    
    // Si ya está autenticado, redirigir al dashboard
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/dashboard']);
    }
  }

  setActiveTab(tab: 'signup' | 'login') {
    if (tab === 'signup') {
      this.router.navigate(['/register']);
    }
    this.activeTab.set(tab);
    this.errorMessage.set('');
    this.showSessionDialog.set(false);
  }

  onSubmit() {
    if (this.signupForm.valid) {
      this.isLoading.set(true);
      this.errorMessage.set('');

      const { email, password } = this.signupForm.value;

      this.authService.login(email, password).subscribe({
        next: (response) => {
          this.isLoading.set(false);

          if (response.ok) {
            // Login exitoso
            console.log('Login exitoso:', response);
            
            // Conectar Socket.io
            if (response.token) {
              this.socketService.conectar(response.token);
              // Unirse a conversaciones
              this.socketService.conectado$.subscribe(conectado => {
                if (conectado) {
                  this.chatService.unirseAConversaciones();
                  this.chatService.cargarConversaciones();
                }
              });
            }

            this.notificationService.success(
              '¡Bienvenido!',
              `Has iniciado sesión correctamente, ${response.usuario?.nombre || ''}`
            );
            this.router.navigate(['/dashboard']);
          } else if (response.session_active) {
            // Ya hay una sesión activa en otro dispositivo
            this.showSessionDialog.set(true);
            this.sessionInfo.set({
              fecha: response.session_fecha || '',
              device: response.device_type || 'desconocido'
            });
            this.notificationService.warning(
              'Sesión Activa Detectada',
              'Ya existe una sesión activa en otro dispositivo'
            );
          } else if (response.code === 'PAGO_PENDIENTE_VALIDACION') {
            const msg =
              response.msg ||
              'Tu pago está en validación. Cuando el equipo lo confirme podrás iniciar sesión. Revisa tu correo.';
            this.errorMessage.set(msg);
            this.notificationService.warning('Pago en validación', msg);
          } else {
            // Otro tipo de error
            this.errorMessage.set(response.msg || 'Error al iniciar sesión');
            this.notificationService.error(
              'Error al iniciar sesión',
              response.msg || 'Credenciales inválidas'
            );
          }
        },
        error: (error) => {
          this.isLoading.set(false);
          console.error('Error en login:', error);
          
          // Manejar diferentes tipos de error
          let errorMsg = 'Error de conexión. Verifica tu internet o el servidor.';
          
          if (error.error?.msg) {
            errorMsg = error.error.msg;
          } else if (error.status === 0) {
            errorMsg = 'No se puede conectar con el servidor';
          } else if (error.status === 404) {
            errorMsg = 'Servicio no encontrado';
          } else if (error.status >= 500) {
            errorMsg = 'Error en el servidor';
          }
          
          this.errorMessage.set(errorMsg);
          this.notificationService.error('Error', errorMsg);
        }
      });
    } else {
      // Marcar todos los campos como tocados para mostrar errores
      Object.keys(this.signupForm.controls).forEach(key => {
        this.signupForm.get(key)?.markAsTouched();
      });
    }
  }

  closeSessionDialog() {
    this.showSessionDialog.set(false);
    this.sessionInfo.set(null);
    this.errorMessage.set('');
    this.isLoading.set(false);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
